import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AudioLines, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { api, type ExtractVideoAudioMode, type ExtractVideoAudioResult } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import type { ToolbarAction } from './AgentNodeToolbar'

const extractAudioLog = createNadDebug('dw:audio-extraction-to-sound-expert')
const SUPPORTED_EXTRACT_AUDIO_SOURCE_TYPES = new Set(['video_generator', 'video'])

const EXTRACT_AUDIO_OPTIONS: Array<{
  key: string
  mode: ExtractVideoAudioMode
  labelKey: string
}> = [
  { key: 'extract-audio-raw', mode: 'raw', labelKey: 'videoGeneratorNode.extractAudioRaw' },
  {
    key: 'extract-audio-isolated-stems',
    mode: 'isolated_stems',
    labelKey: 'videoGeneratorNode.extractAudioStems',
  },
  {
    key: 'extract-audio-dialogues',
    mode: 'dialogues',
    labelKey: 'videoGeneratorNode.extractAudioDialogues',
  },
]

export function useExtractAudioAction({
  nodeId,
  sourceNodeType,
  hasVideo,
  onRunningModeChange,
}: {
  nodeId: string
  sourceNodeType: string
  hasVideo: boolean
  onRunningModeChange?: (mode: ExtractVideoAudioMode | null) => void
}): ToolbarAction {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const [isExtractingAudio, setIsExtractingAudio] = useState(false)
  const inFlight = useRef(false)
  const mountedRef = useRef(true)
  const runGenRef = useRef(0)
  const isSupportedSourceType = SUPPORTED_EXTRACT_AUDIO_SOURCE_TYPES.has(sourceNodeType)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      runGenRef.current += 1
    }
  }, [])

  useEffect(() => {
    extractAudioLog({
      event: 'audio-extraction:eligibility',
      nodeId,
      sourceNodeType,
      hasVideo,
      supported: isSupportedSourceType,
    })
  }, [hasVideo, isSupportedSourceType, nodeId, sourceNodeType])

  const extractAudio = useCallback(
    async (mode: ExtractVideoAudioMode) => {
      if (!projectId || !hasVideo || !isSupportedSourceType || inFlight.current) return

      const runGen = (runGenRef.current += 1)
      const stale = () => !mountedRef.current || runGenRef.current !== runGen
      inFlight.current = true
      setIsExtractingAudio(true)
      onRunningModeChange?.(mode)
      extractAudioLog({
        event: 'audio-extraction:start',
        project_id: projectId,
        canvas_id: canvasId,
        nodeId,
        mode,
      })

      try {
        const result: ExtractVideoAudioResult = await api.extractVideoAudioToSoundExpert(
          projectId,
          nodeId,
          canvasId,
          mode,
        )
        if (stale()) return
        extractAudioLog({
          event: 'audio-extraction:success',
          project_id: projectId,
          canvas_id: canvasId,
          nodeId,
          mode,
          status: result.status,
          splitJobId: result.jobId,
          soundNodeIds: result.soundNodeIds,
        })
        toast.success(t('videoGeneratorNode.extractAudioCreated', { count: result.soundNodeIds.length }))
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        extractAudioLog({
          event: 'audio-extraction:error',
          project_id: projectId,
          canvas_id: canvasId,
          nodeId,
          mode,
          error: message,
        })
        toast.error(t('videoGeneratorNode.extractAudioFailed', { message }))
      } finally {
        if (!stale()) {
          inFlight.current = false
          setIsExtractingAudio(false)
          onRunningModeChange?.(null)
        }
      }
    },
    [projectId, canvasId, nodeId, hasVideo, isSupportedSourceType, onRunningModeChange, t],
  )

  return useMemo(
    () => ({
      key: 'extractAudio',
      label: t('videoGeneratorNode.extractAudio'),
      hidden: !isSupportedSourceType,
      icon: isExtractingAudio
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : <AudioLines className="w-3.5 h-3.5" />,
      disabled: !projectId || !hasVideo || !isSupportedSourceType || isExtractingAudio,
      menuItems: EXTRACT_AUDIO_OPTIONS.map((option) => ({
        key: option.key,
        label: t(option.labelKey),
        hidden: !isSupportedSourceType,
        onClick: () => {
          void extractAudio(option.mode)
        },
        disabled: !projectId || !hasVideo || !isSupportedSourceType || isExtractingAudio,
      })),
    }),
    [projectId, hasVideo, isSupportedSourceType, isExtractingAudio, extractAudio, t],
  )
}
